import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import ViewListRounded from '@mui/icons-material/ViewListRounded';
import GridViewRounded from '@mui/icons-material/GridViewRounded';

export type ViewMode = 'list' | 'grid';

/** Remembers the chosen layout per page in localStorage. */
export const useViewMode = (storageKey: string, initial: ViewMode = 'grid') => {
  const [mode, setModeState] = useState<ViewMode>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === 'list' || v === 'grid' ? v : initial;
    } catch {
      return initial;
    }
  });
  const setMode = useCallback((m: ViewMode) => {
    setModeState(m);
    try { localStorage.setItem(storageKey, m); } catch { /* ignore */ }
  }, [storageKey]);
  return [mode, setMode] as const;
};

type Props = { value: ViewMode; onChange: (m: ViewMode) => void };

const options: { value: ViewMode; label: string; Icon: typeof ViewListRounded }[] = [
  { value: 'grid', label: 'Grid view', Icon: GridViewRounded },
  { value: 'list', label: 'List view', Icon: ViewListRounded },
];

/** Glossy pill switch (grid | list) that follows the current section colours. */
const ViewToggle = ({ value, onChange }: Props) => (
  <Box
    role="group"
    aria-label="Layout"
    sx={{
      display: 'inline-flex', p: 0.5, gap: 0.5, borderRadius: '999px',
      bgcolor: (t) => (t.palette.mode === 'light' ? '#F1EEFB' : 'action.hover'),
      boxShadow: 'inset 0 1px 3px rgba(98,38,167,0.12)',
    }}
  >
    {options.map(({ value: v, label, Icon }) => {
      const active = v === value;
      return (
        <Box
          key={v}
          component="button"
          type="button"
          aria-label={label}
          aria-pressed={active}
          onClick={() => onChange(v)}
          sx={{
            width: 38, height: 34, border: 0, cursor: 'pointer', borderRadius: '999px',
            display: 'grid', placeItems: 'center',
            color: active ? '#fff' : 'text.secondary',
            background: active
              ? 'linear-gradient(135deg, var(--qs-a, #6226A7), var(--qs-b, #F6A938))'
              : 'transparent',
            boxShadow: active ? '0 6px 14px rgba(0,0,0,.22), inset 0 1px 0 rgba(255,255,255,.4)' : 'none',
            transition: 'all .18s ease',
            '&:hover': { bgcolor: active ? undefined : 'action.hover' },
          }}
        >
          <Icon fontSize="small" />
        </Box>
      );
    })}
  </Box>
);

export default ViewToggle;
